package br.com.vvrefeicoes.portal.core.util

sealed interface PdfLine {
    data class Title(
        val text: String,
    ) : PdfLine

    data class Heading(
        val text: String,
    ) : PdfLine

    data class Text(
        val text: String,
    ) : PdfLine

    data class Row(
        val label: String,
        val value: String,
        val bold: Boolean = false,
    ) : PdfLine

    data object Divider : PdfLine

    data object Gap : PdfLine
}

data class PdfReport(
    val lines: List<PdfLine>,
)

data class PdfPageSize(
    val width: Float,
    val height: Float,
) {
    companion object {
        val A4_PORTRAIT = PdfPageSize(width = 595f, height = 842f)
        val A4_LANDSCAPE = PdfPageSize(width = 842f, height = 595f)
    }
}

data class PdfColor(
    val red: Int,
    val green: Int,
    val blue: Int,
) {
    init {
        require(red in 0..255) { "red deve estar entre 0 e 255" }
        require(green in 0..255) { "green deve estar entre 0 e 255" }
        require(blue in 0..255) { "blue deve estar entre 0 e 255" }
    }
}

/** Imagem RGB sem compressão, com [width] x [height] pixels e três bytes por pixel. */
data class PdfImage(
    val width: Int,
    val height: Int,
    val rgb: ByteArray,
) {
    init {
        require(width > 0 && height > 0) { "dimensões da imagem inválidas" }
        require(rgb.size == width * height * 3) { "rgb deve ter width*height*3 bytes" }
    }
}

sealed interface PdfOp {
    data class Rect(
        val x: Float,
        val y: Float,
        val width: Float,
        val height: Float,
        val radius: Float = 0f,
        val fill: PdfColor? = null,
        val stroke: PdfColor? = null,
        val strokeWidth: Float = 1f,
    ) : PdfOp

    data class Line(
        val x1: Float,
        val y1: Float,
        val x2: Float,
        val y2: Float,
        val color: PdfColor,
        val width: Float = 1f,
    ) : PdfOp

    data class Text(
        val x: Float,
        val y: Float,
        val text: String,
        val size: Float,
        val bold: Boolean = false,
        val color: PdfColor = PdfColor(33, 37, 41),
    ) : PdfOp

    data class Image(
        val image: PdfImage,
        val x: Float,
        val y: Float,
        val width: Float,
        val height: Float,
    ) : PdfOp
}

data class PdfLink(
    val x: Float,
    val top: Float,
    val width: Float,
    val height: Float,
    val url: String,
)

data class PdfCanvasPage(
    val ops: List<PdfOp>,
    val links: List<PdfLink> = emptyList(),
)

data class PdfCanvasDocument(
    val pageSize: PdfPageSize,
    val pages: List<PdfCanvasPage>,
)

/** Aproximação de métricas Helvetica usada apenas para posicionar/quebrar textos. */
object PdfText {
    fun width(
        text: String,
        size: Float,
        bold: Boolean = false,
    ): Float {
        var total = 0f
        for (char in text) {
            total += charWidth(char, bold)
        }
        return total * size
    }

    private fun charWidth(
        char: Char,
        bold: Boolean,
    ): Float =
        when (char) {
            ' ' -> 0.278f
            'A', 'Á', 'À', 'Â', 'Ã', 'Ä' -> if (bold) 0.722f else 0.667f
            'B' -> if (bold) 0.722f else 0.667f
            'C', 'Ç' -> 0.722f
            'D' -> 0.722f
            'E', 'É', 'Ê', 'È' -> 0.667f
            'F' -> 0.611f
            'G' -> 0.778f
            'H' -> 0.722f
            'I', 'Í', 'Ì', 'Î' -> 0.278f
            'J' -> if (bold) 0.556f else 0.5f
            'K' -> if (bold) 0.722f else 0.667f
            'L' -> if (bold) 0.611f else 0.556f
            'M' -> 0.833f
            'N' -> 0.722f
            'O', 'Ó', 'Ô', 'Õ', 'Ò' -> 0.778f
            'P' -> 0.667f
            'Q' -> 0.778f
            'R' -> 0.722f
            'S' -> 0.667f
            'T' -> 0.611f
            'U', 'Ú', 'Û', 'Ù' -> 0.722f
            'V' -> 0.667f
            'W' -> 0.944f
            'X' -> 0.667f
            'Y' -> 0.667f
            'Z' -> 0.611f
            'a', 'á', 'à', 'â', 'ã', 'ä' -> 0.556f
            'b' -> if (bold) 0.611f else 0.556f
            'c', 'ç' -> if (bold) 0.556f else 0.5f
            'd' -> if (bold) 0.611f else 0.556f
            'e', 'é', 'ê', 'è' -> 0.556f
            'f' -> if (bold) 0.333f else 0.278f
            'g' -> if (bold) 0.611f else 0.556f
            'h' -> if (bold) 0.611f else 0.556f
            'i', 'l', 'j', 'í', 'ì', 'î' -> if (bold) 0.278f else 0.222f
            'k' -> if (bold) 0.556f else 0.5f
            'm' -> if (bold) 0.889f else 0.833f
            'n' -> if (bold) 0.611f else 0.556f
            'o', 'ó', 'ô', 'õ', 'ò' -> if (bold) 0.611f else 0.556f
            'p' -> if (bold) 0.611f else 0.556f
            'q' -> if (bold) 0.611f else 0.556f
            'r' -> if (bold) 0.389f else 0.333f
            's' -> if (bold) 0.556f else 0.5f
            't' -> if (bold) 0.333f else 0.278f
            'u', 'ú', 'û', 'ù' -> if (bold) 0.611f else 0.556f
            'v' -> if (bold) 0.556f else 0.5f
            'w' -> if (bold) 0.778f else 0.722f
            'x' -> if (bold) 0.556f else 0.5f
            'y' -> if (bold) 0.556f else 0.5f
            'z' -> 0.5f
            in '0'..'9' -> 0.556f
            else -> if (bold) 0.611f else 0.556f
        }

    fun wrap(
        text: String,
        maxWidth: Float,
        size: Float,
        bold: Boolean = false,
        maxLines: Int = Int.MAX_VALUE,
    ): List<String> {
        if (text.isBlank()) return emptyList()
        val lines = ArrayList<String>()
        var current = StringBuilder()
        for (word in text.trim().split(Regex("\\s+"))) {
            val candidate = if (current.isEmpty()) word else "$current $word"
            if (current.isEmpty() || width(candidate, size, bold) <= maxWidth) {
                current = StringBuilder(candidate)
            } else {
                lines.add(current.toString())
                current = StringBuilder(word)
            }
        }
        if (current.isNotEmpty()) lines.add(current.toString())
        if (lines.size <= maxLines) return lines
        val kept = lines.take(maxLines).toMutableList()
        kept[kept.lastIndex] = ellipsize(kept.last() + "...", maxWidth, size, bold)
        return kept
    }

    fun ellipsize(
        text: String,
        maxWidth: Float,
        size: Float,
        bold: Boolean = false,
    ): String {
        if (width(text, size, bold) <= maxWidth) return text
        val suffix = "..."
        var end = text.length
        while (end > 0 && width(text.substring(0, end) + suffix, size, bold) > maxWidth) {
            end--
        }
        return text.substring(0, end) + suffix
    }
}

/**
 * Gerador mínimo de PDF com dois modos:
 *
 * - [PdfReport]: fluxo vertical de texto/linhas em A4 retrato (relatórios simples);
 * - [PdfCanvasDocument]: canvas absoluto com página configurável, formas e imagens.
 *
 * Usa as fontes base Helvetica/Helvetica-Bold com WinAnsiEncoding, suficiente para
 * acentuação em português. Sem dependências externas.
 */
object PdfWriter {
    private const val MARGIN = 50f

    fun build(report: PdfReport): ByteArray {
        val content = renderReport(report)
        return assemble(PdfPageSize.A4_PORTRAIT, listOf(content), emptyList(), listOf(emptyList()))
    }

    fun build(document: PdfCanvasDocument): ByteArray {
        val images = LinkedHashMap<PdfImage, String>()
        val contents = document.pages.map { renderPage(it, images) }
        return assemble(
            pageSize = document.pageSize,
            contents = contents,
            images = images.keys.toList(),
            links = document.pages.map { it.links },
        )
    }

    private fun renderReport(report: PdfReport): String {
        val pageWidth = PdfPageSize.A4_PORTRAIT.width
        val pageHeight = PdfPageSize.A4_PORTRAIT.height
        val maxY = pageHeight - MARGIN
        val minY = MARGIN
        val rightX = pageWidth - MARGIN

        val pages = ArrayList<StringBuilder>()
        var current = StringBuilder()
        pages.add(current)
        var y = maxY

        fun ensureSpace(needed: Float) {
            if (y - needed < minY) {
                current = StringBuilder()
                pages.add(current)
                y = maxY
            }
        }

        fun drawText(
            x: Float,
            value: String,
            size: Float,
            bold: Boolean,
        ) {
            current.append(textCommand(x, y, value, size, bold, PdfColor(0, 0, 0)))
        }

        for (line in report.lines) {
            when (line) {
                is PdfLine.Title -> {
                    ensureSpace(24f)
                    drawText(x = MARGIN, value = line.text, size = 16f, bold = true)
                    y -= 24f
                }
                is PdfLine.Heading -> {
                    ensureSpace(20f)
                    drawText(x = MARGIN, value = line.text, size = 12f, bold = true)
                    y -= 20f
                }
                is PdfLine.Text -> {
                    ensureSpace(14f)
                    drawText(x = MARGIN, value = line.text, size = 10f, bold = false)
                    y -= 14f
                }
                is PdfLine.Row -> {
                    ensureSpace(14f)
                    drawText(x = MARGIN, value = line.label, size = 10f, bold = line.bold)
                    val valueWidth = line.value.length * 5.4f
                    drawText(x = rightX - valueWidth, value = line.value, size = 10f, bold = line.bold)
                    y -= 14f
                }
                PdfLine.Divider -> {
                    ensureSpace(10f)
                    current.append(lineCommand(MARGIN, y, rightX, y, PdfColor(204, 204, 204), 0.5f))
                    y -= 10f
                }
                PdfLine.Gap -> {
                    ensureSpace(8f)
                    y -= 8f
                }
            }
        }

        return pages.joinToString(separator = "") { it.toString() }
    }

    private fun renderPage(
        page: PdfCanvasPage,
        images: MutableMap<PdfImage, String>,
    ): String {
        val current = StringBuilder()
        for (op in page.ops) {
            when (op) {
                is PdfOp.Rect -> appendRect(current, op)
                is PdfOp.Line -> current.append(lineCommand(op.x1, op.y1, op.x2, op.y2, op.color, op.width))
                is PdfOp.Text -> current.append(textCommand(op.x, op.y, op.text, op.size, op.bold, op.color))
                is PdfOp.Image -> {
                    val name = images.getOrPut(op.image) { "Im${images.size + 1}" }
                    current.append("q ")
                    current.append("${number(op.width)} 0 0 ${number(op.height)} ")
                    current.append("${number(op.x)} ${number(op.y)} cm /")
                    current.append(name)
                    current.append(" Do Q\n")
                }
            }
        }
        return current.toString()
    }

    private fun appendRect(
        current: StringBuilder,
        op: PdfOp.Rect,
    ) {
        val radius = op.radius.coerceIn(0f, minOf(op.width, op.height) / 2f)
        if (radius <= 0f) {
            current.append("${number(op.x)} ${number(op.y)} ${number(op.width)} ${number(op.height)} re\n")
        } else {
            appendRoundedRectPath(current, op.x, op.y, op.width, op.height, radius)
        }
        val fill = op.fill
        val stroke = op.stroke
        if (fill != null) current.append(rgbCommand(fill)).append(" rg\n")
        if (stroke != null) {
            current.append(rgbCommand(stroke)).append(" RG\n")
            current.append(number(op.strokeWidth)).append(" w\n")
        }
        when {
            fill != null && stroke != null -> current.append("B\n")
            fill != null -> current.append("f\n")
            stroke != null -> current.append("S\n")
        }
    }

    private fun appendRoundedRectPath(
        current: StringBuilder,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        radius: Float,
    ) {
        val k = radius * 0.5523f
        current.append("${number(x + radius)} ${number(y)} m\n")
        current.append("${number(x + width - radius)} ${number(y)} l\n")
        current.append(
            "${number(x + width - radius + k)} ${number(y)} " +
                "${number(x + width)} ${number(y + radius - k)} " +
                "${number(x + width)} ${number(y + radius)} c\n",
        )
        current.append("${number(x + width)} ${number(y + height - radius)} l\n")
        current.append(
            "${number(x + width)} ${number(y + height - radius + k)} " +
                "${number(x + width - radius + k)} ${number(y + height)} " +
                "${number(x + width - radius)} ${number(y + height)} c\n",
        )
        current.append("${number(x + radius)} ${number(y + height)} l\n")
        current.append(
            "${number(x + radius - k)} ${number(y + height)} " +
                "${number(x)} ${number(y + height - radius + k)} " +
                "${number(x)} ${number(y + height - radius)} c\n",
        )
        current.append("${number(x)} ${number(y + radius)} l\n")
        current.append(
            "${number(x)} ${number(y + radius - k)} " +
                "${number(x + radius - k)} ${number(y)} " +
                "${number(x + radius)} ${number(y)} c\n",
        )
        current.append("h\n")
    }

    private fun textCommand(
        x: Float,
        y: Float,
        value: String,
        size: Float,
        bold: Boolean,
        color: PdfColor,
    ): String {
        val font = if (bold) "/F2" else "/F1"
        return "BT $font ${number(size)} Tf ${rgbCommand(color)} rg " +
            "${number(x)} ${number(y)} Td (${escape(value)}) Tj ET\n"
    }

    private fun lineCommand(
        x1: Float,
        y1: Float,
        x2: Float,
        y2: Float,
        color: PdfColor,
        width: Float,
    ): String =
        "${rgbCommand(color)} RG ${number(width)} w " +
            "${number(x1)} ${number(y1)} m ${number(x2)} ${number(y2)} l S\n"

    private fun assemble(
        pageSize: PdfPageSize,
        contents: List<String>,
        images: List<PdfImage>,
        links: List<List<PdfLink>>,
    ): ByteArray {
        val pageContents = contents.ifEmpty { listOf("") }
        val pageCount = pageContents.size
        val pageLinks = List(pageCount) { index -> links.getOrElse(index) { emptyList() } }
        val linkCount = pageLinks.sumOf { it.size }
        val fontNormal = 3 + pageCount * 2
        val fontBold = fontNormal + 1
        val firstImage = fontNormal + 2
        val firstLink = firstImage + images.size
        val objectCount = fontBold + images.size + linkCount
        val linkIdsByPage = mutableListOf<List<Int>>()
        var nextLink = firstLink
        pageLinks.forEach { pageLinkList ->
            linkIdsByPage += pageLinkList.indices.map { nextLink++ }
        }

        val sink = ByteSink()
        val offsets = IntArray(objectCount + 1)

        fun beginObject(number: Int) {
            offsets[number] = sink.size
            sink.writeAscii("$number 0 obj\n")
        }

        fun endObject() {
            sink.writeAscii("endobj\n")
        }

        sink.writeAscii("%PDF-1.4\n")

        beginObject(1)
        sink.writeAscii("<< /Type /Catalog /Pages 2 0 R >>\n")
        endObject()

        val kids = (0 until pageCount).joinToString(" ") { "${3 + it * 2} 0 R" }
        beginObject(2)
        sink.writeAscii("<< /Type /Pages /Kids [$kids] /Count $pageCount >>\n")
        endObject()

        val xObjectResources =
            if (images.isEmpty()) {
                ""
            } else {
                val entries = images.indices.joinToString(" ") { "/Im${it + 1} ${firstImage + it} 0 R" }
                " /XObject << $entries >>"
            }

        for (index in 0 until pageCount) {
            val pageObject = 3 + index * 2
            val contentObject = pageObject + 1
            beginObject(pageObject)
            sink.writeAscii("<< /Type /Page /Parent 2 0 R ")
            sink.writeAscii("/MediaBox [0 0 ${number(pageSize.width)} ${number(pageSize.height)}] ")
            sink.writeAscii("/Resources << /Font << /F1 $fontNormal 0 R /F2 $fontBold 0 R >>")
            sink.writeAscii("$xObjectResources >> ")
            val annots = linkIdsByPage[index]
            if (annots.isNotEmpty()) {
                sink.writeAscii("/Annots [${annots.joinToString(" ") { "$it 0 R" }}] ")
            }
            sink.writeAscii("/Contents $contentObject 0 R >>\n")
            endObject()
            val contentBytes = encodeWinAnsi(pageContents[index])
            beginObject(contentObject)
            sink.writeAscii("<< /Length ${contentBytes.size} >>\nstream\n")
            sink.writeBytes(contentBytes)
            sink.writeAscii("\nendstream\n")
            endObject()
        }

        beginObject(fontNormal)
        sink.writeAscii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>\n")
        endObject()

        beginObject(fontBold)
        sink.writeAscii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>\n")
        endObject()

        for ((index, image) in images.withIndex()) {
            beginObject(firstImage + index)
            sink.writeAscii("<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} ")
            sink.writeAscii("/ColorSpace /DeviceRGB /BitsPerComponent 8 /Length ${image.rgb.size} >>\nstream\n")
            sink.writeBytes(image.rgb)
            sink.writeAscii("\nendstream\n")
            endObject()
        }

        var linkObject = firstLink
        pageLinks.forEach { pageLinkList ->
            pageLinkList.forEach { link ->
                val bottom = pageSize.height - link.top - link.height
                beginObject(linkObject)
                sink.writeAscii("<< /Type /Annot /Subtype /Link ")
                sink.writeAscii("/Rect [${number(link.x)} ${number(bottom)} ${number(link.x + link.width)} ${number(bottom + link.height)}] ")
                sink.writeAscii("/Border [0 0 0] ")
                sink.writeAscii("/A << /S /URI /URI (${escape(link.url)}) >> >>\n")
                endObject()
                linkObject += 1
            }
        }

        val xrefOffset = sink.size
        sink.writeAscii("xref\n0 ${objectCount + 1}\n")
        sink.writeAscii("0000000000 65535 f \n")
        for (number in 1..objectCount) {
            sink.writeAscii(offsets[number].toString().padStart(10, '0'))
            sink.writeAscii(" 00000 n \n")
        }
        sink.writeAscii("trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\n")
        sink.writeAscii("startxref\n$xrefOffset\n%%EOF\n")

        return sink.toByteArray()
    }

    private fun rgbCommand(color: PdfColor): String = "${colorComponent(color.red)} ${colorComponent(color.green)} ${colorComponent(color.blue)}"

    private fun colorComponent(value: Int): String {
        val thousandths = (value * 1000 + 127) / 255
        return (thousandths / 1000.0).toString()
    }

    private fun number(value: Float): String {
        val rounded = kotlin.math.round(value * 1000f) / 1000f
        return if (rounded == rounded.toInt().toFloat()) rounded.toInt().toString() else rounded.toString()
    }

    private fun escape(value: String): String =
        value
            .replace("\\", "\\\\")
            .replace("(", "\\(")
            .replace(")", "\\)")

    private fun encodeWinAnsi(value: String): ByteArray {
        val bytes = ByteArray(value.length)
        for (index in value.indices) {
            val code = value[index].code
            bytes[index] = if (code in 0..255) code.toByte() else '?'.code.toByte()
        }
        return bytes
    }

    private class ByteSink {
        private var buffer = ByteArray(1024)
        private var length = 0

        val size: Int
            get() = length

        private fun ensure(extra: Int) {
            if (length + extra <= buffer.size) return
            var capacity = buffer.size
            while (capacity < length + extra) capacity *= 2
            buffer = buffer.copyOf(capacity)
        }

        fun writeAscii(value: String) {
            ensure(value.length)
            for (char in value) {
                buffer[length++] = char.code.toByte()
            }
        }

        fun writeBytes(value: ByteArray) {
            ensure(value.size)
            value.copyInto(buffer, length)
            length += value.size
        }

        fun toByteArray(): ByteArray = buffer.copyOf(length)
    }
}
